import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { Checkbox, ListItemText } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import { IconSvg } from 'components/atoms';
import FormHelperText from '@material-ui/core/FormHelperText';
import Bashkia from '../../../assets/icons/Statistics/bashkite.svg';
import Viti from '../../../assets/icons/Statistics/viti-01.svg';
import Periudha from '../../../assets/icons/Statistics/periudha-01.svg';
import LlojiTeDhenave from '../../../assets/icons/Statistics/te-dhenat.svg';

const images = { Bashkia, Viti, Periudha, LlojiTeDhenave };

const useStyles = makeStyles((theme) => ({
  select: {
    borderRadius: 999,
  },
  selectRoot: {
    padding: theme.spacing(1),
    fontFamily: 'Roboto',
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    boxShadow: '0px 3px 6px #00000029',
    borderRadius: '999px',
    width: '250px',
    '&:focus': {
      borderRadius: '999px',
    },
  },
  selectOutlined: {
    borderRadius: '999px !important',
  },
  selectCheckbox: {
    padding: theme.spacing(0.25),
  },
  removeTop: {
    top: '16px !important',
  },
  selectAllText: {
    fontWeight: 500,
  },
  selectIcon: {
    color: 'black',
  },
}));

const SimpleMultiSelect = (props) => {
  const {
    items,
    placeholder,
    startText,
    name,
    value,
    onChange,
    icon,
    error,
    filterTypeError,
    imageName,
    ...others
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const isAllSelected = items?.length > 0 && value?.length === items?.length;

  const [selected, setSelected] = useState([]);

  const handleChange = (event) => {
    const value = event.target.value;
    if (value[value.length - 1] === 'all') {
      const newSelectedValue = selected.length === items?.length ? [] : items.map((x) => x.value);
      setSelected(newSelectedValue);
      onChange({ target: { value: newSelectedValue } });
      return;
    }
    setSelected(value);
    onChange(event);
  };

  useEffect(() => {
    if (!value) setSelected([]);
    else setSelected(value);
  }, [value, items]);

  return (
    <FormControl className={classes.formControl}>
      <Select
        {...others}
        name={name}
        value={selected || []}
        displayEmpty
        variant='standard'
        className={classes.select}
        multiple
        disableUnderline
        renderValue={(selected) => {
          if (selected?.length === 0 || !selected) {
            return (
              <Grid container>
                <Grid item xs={1} style={{ margin: 'auto 0' }}>
                  {imageName ? (
                    <img src={images[imageName]} alt={imageName} />
                  ) : (
                    <IconSvg name={icon} viewBox='0 0 21.397 17.342' className={classes.icon} />
                  )}
                </Grid>
                <Grid item xs={10} style={{ margin: 'auto 6px', fontWeight: '500', fontSize: '14px' }}>
                  {placeholder}
                </Grid>
              </Grid>
            );
          }

          const selectedItems = Array.isArray(selected)
            ? items.filter((i) => selected.includes(i.value))
            : items.filter((i) => i.value === selected);

          return (
            <Grid container>
              <Grid item xs={1} style={{ margin: 'auto 0' }}>
                {imageName ? (
                  <img src={images[imageName]} alt={imageName} />
                ) : (
                  <IconSvg name={icon} viewBox='0 0 21.397 17.342' className={classes.icon} />
                )}
              </Grid>
              <Grid
                item
                xs={10}
                style={{
                  margin: 'auto 6px',
                  fontWeight: '500',
                  fontSize: '14px',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                {[startText || '', ': ', selectedItems.map((i) => i.description).join(', ')].join('')}
              </Grid>
            </Grid>
          );
        }}
        classes={{
          root: classes.selectRoot,
          outlined: classes.selectOutlined,
          icon: classes.selectIcon,
        }}
        MenuProps={{
          PaperProps: {
            className: window.innerHeight < items.length * 44 ? classes.removeTop : null,
          },
        }}
        onChange={handleChange}
      >
        <MenuItem value={undefined}>
          <em>{placeholder}</em>
        </MenuItem>

        {items
          ?.filter((el) => el.value === -1)
          .map((item) => (
            <MenuItem
              key={item.value}
              value={item.value}
              disabled={(selected?.length > 0 && selected[0] !== -1) || selected?.length === items?.length}
            >
              <Checkbox
                className={classes.selectCheckbox}
                checked={selected?.indexOf(item.value) > -1 && !isAllSelected}
              />
              <ListItemText primary={item.description} />
            </MenuItem>
          ))}

        <MenuItem value='all' disabled={selected.length === 1 && selected[0] === -1}>
          <Checkbox
            className={classes.selectCheckbox}
            checked={isAllSelected}
            indeterminate={selected?.length > 0 && selected?.length < items?.length}
          />
          <ListItemText classes={{ primary: classes.selectAllText }} primary={t('COMMON.SELECT_ALL')} />
        </MenuItem>

        {items
          ?.filter((el) => el.value !== -1)
          .map((item) => (
            <MenuItem
              key={item.value}
              value={item.value}
              style={{ marginLeft: '20px' }}
              disabled={selected.length === 1 && selected[0] === -1}
            >
              <Checkbox className={classes.selectCheckbox} checked={selected?.indexOf(item.value) > -1} />
              <ListItemText primary={item.description} />
            </MenuItem>
          ))}
      </Select>
      {error && <FormHelperText>{t('STATISTICS.REQUIRED_FIELD')}</FormHelperText>}
      {filterTypeError && <FormHelperText>{t('STATISTICS.REQUIRED_FILTER_TYPE')}</FormHelperText>}
    </FormControl>
  );
};

SimpleMultiSelect.propTypes = {
  ...Select.propTypes,
  /**
   * External classes
   */
  className: PropTypes.string,
  /**
   * Placeholder
   */
  placeholder: PropTypes.string,
  /**
   * Text to show before the selected value on input
   */
  startText: PropTypes.string,
  /**
   * Item source data as shaped value | description
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      description: PropTypes.string.isRequired,
    }),
  ).isRequired,

  multiple: PropTypes.bool,
};

export default SimpleMultiSelect;
